import React, { Fragment, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DateRangePicker } from "@material-ui/pickers";
import * as C from "components";
import { setCurrentPage, getPicTreeRegularSeasonEventBootstrapData, getPicTreeRegularSeasonEventAreaData } from "actions";
import SeasonSelector from "components/PicTree/RegularSeason/SeasonSelector";
import ByAreaSummary from "components/PicTree/Game/Area/ByAreaSummary";
import ByAreaDetailTable from "components/PicTree/Game/Area/ByAreaDetailTable";
import SingleSelect from "components/Select/SingleSelect";
import { EVENT_AREA_LAYER_OPTIONS } from "./PicTreeRegularSeasonEventByAreaFixtures";

const ALL_BOOST_EVENTS_VALUE = "__ALL__";

const useStyles = makeStyles((theme) => ({
  breadcrumbs: { marginTop: 16, minHeight: 30 },
  topBar: { marginTop: -16, marginBottom: 8, flexWrap: "nowrap" },
  headerTitle: { flex: "0 0 auto" },
  filters: { display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "nowrap", gap: theme.spacing(1), minWidth: 0, overflowX: "auto" },
  eventSelect: { minWidth: 250, maxWidth: 330, flexShrink: 0 },
  dateRange: { display: "flex", alignItems: "center", width: 276, flexShrink: 0 },
  dateInput: { display: "flex", alignItems: "baseline" },
  dateField: { width: 120 },
  delimiter: { margin: "0 4px", color: "#556070" },
  goButton: { height: 32, minWidth: 60, color: "#fff", flexShrink: 0 },
  layerLabel: { color: "#556070", whiteSpace: "nowrap" },
}));

const PicTreeRegularSeasonEventByArea = ({
  setCurrentPage,
  getPicTreeRegularSeasonEventBootstrapData,
  selectedSeasonId = "cmunxinty000079zc6zf595vs",
  selectedSeasonIds = ["cmunxinty000079zc6zf595vs"],
  seasonOptions = [],
  companyOptions = [],
  selectedCompanyCode = "NTT_ME",
  selectedBoostEventId = ALL_BOOST_EVENTS_VALUE,
  selectedDateRange = [null, null],
  eventRows = [],
  eventRowsLoading = false,
  bootstrapEventReady = false,
  getPicTreeRegularSeasonEventAreaData,
  areaSummary = null,
  areaTable = [],
  areaLoading = false,
  areaError = null,
}) => {
  const classes = useStyles();
  const [seasonIds, setSeasonIds] = useState(selectedSeasonIds.length ? selectedSeasonIds : [selectedSeasonId]);
  const [eventId, setEventId] = useState(selectedBoostEventId);
  const [draftRange, setDraftRange] = useState([null, null]);
  const [layerId, setLayerId] = useState("regular_1_l1");

  useEffect(() => {
    setCurrentPage(window.location.pathname);
    getPicTreeRegularSeasonEventBootstrapData({
      selectedSeasonId,
      selectedSeasonIds: [selectedSeasonId],
    });
  }, [getPicTreeRegularSeasonEventBootstrapData, selectedSeasonId, setCurrentPage]);

  useEffect(() => {
    if (selectedBoostEventId && selectedBoostEventId !== ALL_BOOST_EVENTS_VALUE) {
      setEventId(selectedBoostEventId);
    }
  }, [selectedBoostEventId]);

  useEffect(() => {
    if (selectedDateRange?.[0] && selectedDateRange?.[1]) {
      setDraftRange([
        moment(selectedDateRange[0], "YYYY-MM-DD"),
        moment(selectedDateRange[1], "YYYY-MM-DD"),
      ]);
    }
  }, [selectedDateRange]);

  useEffect(() => {
    if (!bootstrapEventReady || !selectedBoostEventId || selectedBoostEventId === ALL_BOOST_EVENTS_VALUE
      || !selectedDateRange?.[0] || !selectedDateRange?.[1]) return;
    getPicTreeRegularSeasonEventAreaData({
      selectedSeasonId,
      selectedSeasonIds: [selectedSeasonId],
      selectedBoostEventId,
      selectedAreaLayerId: layerId,
      selectedDateRange,
    });
  }, [bootstrapEventReady, selectedBoostEventId, selectedDateRange, selectedSeasonId, layerId, getPicTreeRegularSeasonEventAreaData]);

  const options = useMemo(() => (eventRows || []).filter((row, index, rows) => (
    row?.id && rows.findIndex((candidate) => candidate.id === row.id) === index
  )), [eventRows]);
  const selectedCompany = companyOptions.find((item) => item.value === selectedCompanyCode);
  const effectiveEventId = eventId === ALL_BOOST_EVENTS_VALUE ? "" : eventId;

  const handleSeasonChange = (event) => {
    const nextSeasonId = Array.isArray(event.target.value) ? event.target.value[0] : event.target.value;
    setSeasonIds(nextSeasonId ? [nextSeasonId] : []);
    setEventId(ALL_BOOST_EVENTS_VALUE);
    setDraftRange([null, null]);
    if (nextSeasonId) {
      getPicTreeRegularSeasonEventBootstrapData({
        selectedSeasonId: nextSeasonId,
        selectedSeasonIds: [nextSeasonId],
      });
    }
  };

  const handleGo = () => {
    if (!effectiveEventId || !draftRange?.[0] || !draftRange?.[1]) return;
    const nextRange = [
      moment(draftRange[0]).format("YYYY-MM-DD"),
      moment(draftRange[1]).format("YYYY-MM-DD"),
    ];
    getPicTreeRegularSeasonEventAreaData({
      selectedSeasonId,
      selectedSeasonIds: [selectedSeasonId],
      selectedBoostEventId: effectiveEventId,
      selectedAreaLayerId: layerId,
      selectedDateRange: nextRange,
    });
  };

  return (
    <Fragment>
      <C.ContentWrapper>
        <Grid container spacing={1} className={classes.topBar} justifyContent="space-between" alignItems="center">
          <Grid item className={classes.headerTitle}>
            <Breadcrumbs separator="›" className={classes.breadcrumbs}>
              <Typography>PicTrée 全体</Typography>
              <Typography>レギュラーシーズン</Typography>
              <Typography>イベント</Typography>
              <Typography>エリア別</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs className={classes.filters}>
            <SeasonSelector
              selectedSeasonIds={seasonIds}
              seasonOptions={seasonOptions}
              selectedCompanyLabel={selectedCompany?.label || "NTT ME"}
              onSeasonChange={handleSeasonChange}
              showCheckboxes={false}
              allowMultiple={false}
              selectMinWidth={220}
              selectMaxWidth={300}
            />
            <FormControl variant="outlined" size="small" className={classes.eventSelect}>
              <Select
                displayEmpty
                value={options.some((option) => option.id === effectiveEventId) ? effectiveEventId : ""}
                onChange={(event) => setEventId(event.target.value || ALL_BOOST_EVENTS_VALUE)}
                disabled={eventRowsLoading}
                renderValue={(value) => options.find((option) => option.id === value)?.name || "イベントを選択"}
              >
                {options.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    <ListItemText primary={option.name || option.id} secondary={option.period || undefined} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <span className={classes.layerLabel}>階層：</span>
            <SingleSelect
              selected={{ value: layerId }}
              options={EVENT_AREA_LAYER_OPTIONS}
              handleChange={(event) => setLayerId(event.target.value)}
            />
            <div className={classes.dateRange}>
              <DateRangePicker
                startText="Start"
                endText="End"
                disableHighlightToday
                showToolbar={false}
                allowSameDateSelection
                maxDate={moment().format("YYYY-MM-DD")}
                value={draftRange}
                onChange={setDraftRange}
                renderInput={(startProps, endProps) => (
                  <div className={classes.dateInput}>
                    <TextField {...startProps} error={false} size="small" className={classes.dateField} helperText="" />
                    <span className={classes.delimiter}>~</span>
                    <TextField {...endProps} error={false} size="small" className={classes.dateField} helperText="" />
                  </div>
                )}
              />
            </div>
            <Button variant="contained" color="primary" className={classes.goButton} disableElevation onClick={handleGo}>GO</Button>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            {areaLoading && <Typography color="textSecondary">データ取得中です。</Typography>}
            {areaError && <Typography color="error">{areaError}</Typography>}
            <ByAreaSummary data={areaSummary} />
          </CardContent>
        </Card>
        <Grid container spacing={1} style={{ marginTop: 16 }}>
          <Grid item xs={12}>
            <ByAreaDetailTable
              data={areaTable}
              selectedAreaLayerId={layerId}
              rowsPerPage={200}
            />
          </Grid>
        </Grid>
        {/* <Typography variant="caption" color="textSecondary">
          適用期間: {draftRange?.[0] ? moment(draftRange[0]).format("YYYY-MM-DD") : "-"} ~ {draftRange?.[1] ? moment(draftRange[1]).format("YYYY-MM-DD") : "-"}、対象イベント: {effectiveEventId || "未選択"}
        </Typography> */}
      </C.ContentWrapper>
    </Fragment>
  );
};

PicTreeRegularSeasonEventByArea.propTypes = {
  setCurrentPage: PropTypes.func,
  getPicTreeRegularSeasonEventBootstrapData: PropTypes.func,
  selectedSeasonId: PropTypes.string,
  selectedSeasonIds: PropTypes.array,
  seasonOptions: PropTypes.array,
  companyOptions: PropTypes.array,
  selectedCompanyCode: PropTypes.string,
  selectedBoostEventId: PropTypes.string,
  selectedDateRange: PropTypes.array,
  eventRows: PropTypes.array,
  eventRowsLoading: PropTypes.bool,
  bootstrapEventReady: PropTypes.bool,
  getPicTreeRegularSeasonEventAreaData: PropTypes.func,
  areaSummary: PropTypes.object,
  areaTable: PropTypes.array,
  areaLoading: PropTypes.bool,
  areaError: PropTypes.string,
};

const mapStateToProps = (state) => ({
  selectedSeasonId: state.pictreeRegularSeasonEvent.selectedSeasonId,
  selectedSeasonIds: state.pictreeRegularSeasonEvent.selectedSeasonIds,
  selectedBoostEventId: state.pictreeRegularSeasonEvent.selectedBoostEventId,
  selectedDateRange: state.pictreeRegularSeasonEvent.selectedDateRange,
  seasonOptions: state.pictreeRegularSeasonEvent.seasonOptions,
  companyOptions: state.pictreeRegularSeasonEvent.companyOptions,
  selectedCompanyCode: state.pictreeRegularSeasonEvent.selectedCompanyCode,
  eventRows: state.pictreeRegularSeasonEvent.eventRows,
  eventRowsLoading: state.pictreeRegularSeasonEvent.eventRowsLoading,
  bootstrapEventReady: state.pictreeRegularSeasonEvent.bootstrapEventReady,
  areaSummary: state.pictreeRegularSeasonEvent.areaSummary,
  areaTable: state.pictreeRegularSeasonEvent.areaTable,
  areaLoading: state.pictreeRegularSeasonEvent.areaLoading,
  areaError: state.pictreeRegularSeasonEvent.areaError,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getPicTreeRegularSeasonEventBootstrapData: (param) => dispatch(getPicTreeRegularSeasonEventBootstrapData(param)),
  getPicTreeRegularSeasonEventAreaData: (param) => dispatch(getPicTreeRegularSeasonEventAreaData(param)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PicTreeRegularSeasonEventByArea);
